// Federated staff sign-in: the OAuth 2.0 authorization-code flow with PKCE,
// against the Cognito hosted UI.
//
// Separate from auth.ts rather than folded into it because
// amazon-cognito-identity-js cannot do this flow at all. That SDK speaks the
// user-pool API directly -- SRP, challenge responses, its own token storage --
// and has no notion of an authorization code. So federated tokens arrive outside
// the SDK, are stored under our own keys, and are refreshed by hand here.
// auth.ts joins the two halves back together, so the rest of the app keeps
// seeing one kind of session.
//
// Hand-rolled rather than an OIDC library: what follows is one redirect and two
// fetches. A library for it would be more dependency surface to keep patched
// than the code it replaced.
//
// No client secret, because this bundle is downloadable and a secret shipped
// inside it is not a secret. PKCE takes its place: the verifier is generated per
// sign-in, never leaves this origin until it is exchanged, and makes an
// intercepted authorization code useless on its own.
import { loadConfig, type AdminSsoConfig } from './config';

// What auth.ts needs to build a Session. Deliberately not the Session type
// itself -- auth.ts imports this module, and importing its type back would be a
// cycle for no gain.
export interface FederatedIdentity {
  username: string;
  email?: string;
  idToken: string;
}

// localStorage, matching where the Cognito SDK keeps its own tokens: a federated
// sign-in should survive a reload exactly as a password sign-in does.
const TOKENS_KEY = 'trx-sftp-portal.sso';
// sessionStorage, because the verifier is good for one redirect in one tab and
// should not outlive either.
const PENDING_KEY = 'trx-sftp-portal.sso-pending';

interface StoredTokens {
  idToken: string;
  refreshToken: string;
  // Absolute, so it survives the page being closed and reopened.
  expiresAt: number;
}

// Starts a sign-in by leaving the page. Nothing after the call runs.
export const beginSso = async (sso: AdminSsoConfig, clientId: string): Promise<void> => {
  const verifier = randomUrlSafe(64);
  const state = randomUrlSafe(16);
  sessionStorage.setItem(PENDING_KEY, JSON.stringify({ verifier, state }));

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: redirectUri(sso),
    scope: sso.scopes.join(' '),
    state,
    code_challenge: await challengeFor(verifier),
    code_challenge_method: 'S256',
    // Skips the hosted UI's own account picker and goes straight to Entra.
    // Without it, staff get a Cognito-branded page asking them to choose between
    // a password box and one button.
    identity_provider: sso.providerName,
  });
  window.location.assign(`https://${sso.domain}/oauth2/authorize?${params.toString()}`);
};

// Handles the redirect back. Throws with something worth showing on failure --
// every path here is a sign-in that did not happen, not a broken portal.
export const completeSso = async (
  sso: AdminSsoConfig,
  clientId: string,
  search: string
): Promise<void> => {
  const query = new URLSearchParams(search);

  // Cognito reports a refused sign-in by redirecting back with an error rather
  // than by failing the redirect, so this is the usual shape of "Entra said no".
  const error = query.get('error');
  if (error) {
    throw new Error(query.get('error_description') ?? error);
  }

  const code = query.get('code');
  const pending = sessionStorage.getItem(PENDING_KEY);
  // One use only, and removed before the exchange: a verifier left behind is one
  // a second attempt could pick up and fail with.
  sessionStorage.removeItem(PENDING_KEY);

  if (!code) {
    throw new Error('The sign-in did not come back with an authorization code. Try again.');
  }
  if (!pending) {
    // Typically an old callback URL opened fresh, or a different tab.
    throw new Error('This sign-in link has expired. Start again from the portal.');
  }

  const { verifier, state } = JSON.parse(pending) as { verifier: string; state: string };
  // The state check is what stops someone else's authorization code being fed to
  // this page to sign you in as them.
  if (query.get('state') !== state) {
    throw new Error('The sign-in response did not match this browser. Start again from the portal.');
  }

  const tokens = await tokenRequest(sso, {
    grant_type: 'authorization_code',
    client_id: clientId,
    code,
    redirect_uri: redirectUri(sso),
    code_verifier: verifier,
  });
  if (!tokens.refresh_token) {
    throw new Error('Cognito returned no refresh token, so the session could not be kept.');
  }
  store(tokens.id_token, tokens.refresh_token, tokens.expires_in);
};

// The signed-in federated user, refreshing the tokens if they are about to
// expire. Returns undefined when there is no federated session, which is the
// normal case for a partner or a password-authenticated staff member.
export const federatedIdentity = async (): Promise<FederatedIdentity | undefined> => {
  let tokens = read();
  if (!tokens) return undefined;

  const config = await loadConfig();
  if (!config.adminSso) {
    // SSO was turned off in the deployment while this session was stored. The
    // tokens may still be valid, but the way to renew them is gone.
    clearTokens();
    return undefined;
  }

  // A minute of slack. A token that passes this check and then expires in flight
  // is a request that fails for no visible reason.
  if (tokens.expiresAt - Date.now() < 60_000) {
    const refreshed = await refreshTokens(config.adminSso, config.adminClientId, tokens);
    if (!refreshed) return undefined;
    tokens = refreshed;
  }

  const claims = decodeJwt(tokens.idToken);
  return {
    // Provider-prefixed, e.g. EntraID_someone@example.com. That is the identity
    // the API sees in the token, so it is the one to show.
    username: claims['cognito:username'] ?? claims.sub,
    email: claims.email,
    idToken: tokens.idToken,
  };
};

// Whether the stored session is a federated one. Synchronous, because signOut
// has to choose a path before it does anything.
export const isFederated = (): boolean => read() !== undefined;

export const ssoSignOut = async (): Promise<void> => {
  clearTokens();
  const config = await loadConfig();
  if (!config.adminSso) return;

  // The hosted UI keeps a session cookie of its own, and the provider is
  // configured with IDPSignout, so this ends the Entra session too. Without it,
  // signing out and signing back in returns silently to the same account with no
  // prompt, which does not look like having signed out.
  const params = new URLSearchParams({
    client_id: config.adminClientId,
    // Has to be one of the app client's registered logout URLs, which is why the
    // deployment registers this exact origin.
    logout_uri: window.location.origin,
  });
  window.location.assign(`https://${config.adminSso.domain}/logout?${params.toString()}`);
};

const redirectUri = (sso: AdminSsoConfig): string =>
  `${window.location.origin}${sso.callbackPath}`;

interface TokenResponse {
  id_token: string;
  refresh_token?: string;
  expires_in: number;
}

const tokenRequest = async (
  sso: AdminSsoConfig,
  body: Record<string, string>
): Promise<TokenResponse> => {
  const response = await fetch(`https://${sso.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(body).toString(),
  });

  const text = await response.text();
  if (!response.ok) {
    // The body is usually {"error":"invalid_grant"} and nothing else, so the
    // status code is worth keeping next to it.
    let detail = text;
    try {
      detail = (JSON.parse(text) as { error?: string }).error ?? text;
    } catch {
      // Not JSON; the raw text is the best available detail.
    }
    throw new Error(`Sign-in could not be completed (HTTP ${response.status}: ${detail}).`);
  }
  return JSON.parse(text) as TokenResponse;
};

const refreshTokens = async (
  sso: AdminSsoConfig,
  clientId: string,
  tokens: StoredTokens
): Promise<StoredTokens | undefined> => {
  try {
    const response = await tokenRequest(sso, {
      grant_type: 'refresh_token',
      client_id: clientId,
      refresh_token: tokens.refreshToken,
    });
    // A refresh-token grant returns no new refresh token, so the original is
    // carried forward until it expires on its own.
    return store(response.id_token, tokens.refreshToken, response.expires_in);
  } catch {
    // An expired or revoked refresh token is not an error to show anyone -- it
    // means the session is over. Clearing it lands the user on the login form.
    clearTokens();
    return undefined;
  }
};

const store = (idToken: string, refreshToken: string, expiresIn: number): StoredTokens => {
  const tokens: StoredTokens = {
    idToken,
    refreshToken,
    expiresAt: Date.now() + expiresIn * 1000,
  };
  localStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
  return tokens;
};

const read = (): StoredTokens | undefined => {
  const raw = localStorage.getItem(TOKENS_KEY);
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredTokens>;
    if (!parsed.idToken || !parsed.refreshToken || !parsed.expiresAt) {
      throw new Error('incomplete');
    }
    return parsed as StoredTokens;
  } catch {
    // Truncated or hand-edited storage. Treated as no session rather than
    // failing every page load until someone clears their browser data.
    clearTokens();
    return undefined;
  }
};

const clearTokens = (): void => localStorage.removeItem(TOKENS_KEY);

// Claims only. The signature is not checked here and does not need to be: the
// token came straight from Cognito's token endpoint over TLS, and the API
// verifies it properly before trusting anything in it. This is only to put a
// name in the nav bar.
const decodeJwt = (jwt: string): Record<string, string> => {
  const payload = jwt.split('.')[1];
  if (!payload) throw new Error('Malformed identity token');
  const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  // atob yields bytes, not characters. A name with an accent in it decodes to
  // mojibake without the TextDecoder step.
  const bytes = Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, string>;
};

// crypto.subtle exists only in a secure context, which means https or localhost.
// Both are the only ways this app is served.
const challengeFor = async (verifier: string): Promise<string> =>
  base64Url(
    new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)))
  );

const randomUrlSafe = (bytes: number): string =>
  base64Url(crypto.getRandomValues(new Uint8Array(bytes)));

const base64Url = (bytes: Uint8Array): string =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
